import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import { NextLinkFromReactRouter } from 'UIKit/NextLink'
import { RouteConfig } from 'config/constants/route'
import { UIBreakPointConfig, UIComponentConfig } from 'config/layout'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { TopbarContainer } from 'layout/Components/Topbar'
import { useEffect, useRef } from 'react'
import { useMenuContainer } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { overlayBreakpointMap } from 'theme/overlay'
import { HunnyTheme } from 'theme/types'
import { handleClickElement } from 'utils/clickElement'
import { toggleHtmlScrollable } from 'utils/html'
import CasinoSportSelector from '../CasinoSportSelector'
import { RowCenter } from '../Row'
import MenuContent from './MenuContent'
import MenuSmallContent from './MenuSmallContent'
import BonusHoverTooltip from './component/BonusHoverToolTip'

const StyledContainer = styled(Box)`
  width: 0px;
  min-width: 0px;

  ${({ theme }) => theme.mediaQueries.md} {
    width: ${({ theme }) => theme.topbarHeight};
    min-width: ${({ theme }) => theme.topbarHeight};
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    width: auto;
    min-width: auto;
  }

  z-index: ${({ theme }) => theme.zIndices.menu};
`

const getDesktopMenuWidth = ({ theme, $Open }: { theme: HunnyTheme; $Open: boolean }) => {
  return $Open ? `${theme.navigationWidth}px` : `${theme.navigationSmallWidth}px`
}

const StyledTopbarContainer = styled(TopbarContainer)<{ $Open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 12px;
  position: absolute;
  height: ${({ theme }) => theme.topbarHeight};
  width: ${getDesktopMenuWidth};
  min-width: ${getDesktopMenuWidth};
  overflow: hidden;
`

const StyledInnerContainer = styled(Box)<{ $Open: boolean }>`
  transition: ease ${({ theme }) => theme.transitions.medium};
  width: 100%;
  position: fixed;
  transform: ${({ $Open }) => ($Open ? 'translateX(0)' : 'translateX(-100%)')};

  ${({ theme }) => theme.mediaQueries.sm} {
    transition: ease ${({ theme }) => theme.transitions.fast};
    width: ${({ theme }) => theme.navigationWidth}px;
    transform: ${({ $Open }) => ($Open ? 'translateX(0)' : 'translateX(calc(-100% - 24px))')};
  }

  ${({ theme }) => theme.mediaQueries.md} {
    transform: unset;
    width: ${getDesktopMenuWidth};
    min-width: ${getDesktopMenuWidth};
    margin-left: 0px;
    overflow: unset !important;
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    position: relative;
    margin-left: 0px;
  }
`

const StyledMenuWrapper = styled(Flex)`
  background: ${({ theme }) => theme.colors.background};
  width: 100%;
  height: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding-bottom: 0px;
  }
`

const StyledMenuIconContainer = styled(Box)<{ $Open: boolean }>`
  display: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  position: absolute;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  right: 0px;
  top: 50%;
  transform: ${({ $Open }) => ($Open ? 'translate(0%, -50%)' : 'translate(50%, -50%)')};
  background: ${({ theme }) => theme.colors.background};

  ${({ theme }) => theme.mediaQueries.sm} {
    display: flex;
  }

  svg {
    scale: 1.05;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    svg {
      transform: rotate(${({ $Open }) => ($Open ? 0 : 180)}deg);
    }
  }

  svg {
    transition: ease ${({ theme }) => theme.transitions.fast};
    fill: ${({ theme }) => theme.colors.textSubtle};
  }

  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.text};
    }

    ${RowCenter} {
      background-color: ${({ theme }) => theme.modal.colors.darkblue.modalCard};
      border-radius: 50%;
    }
  }
`

const StyledMenuIconMobileContainer = styled(StyledMenuIconContainer)`
  height: 56px;
  width: 56px;
  right: 0px;
  top: 50%;
  transform: translate(0px, -50%);
  display: flex;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: none;
  }
`

const StyledMenuContent = styled(Box)`
  flex: 1 1;
  height: 100%;

  ::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover {
    ::-webkit-scrollbar-thumb {
      background-color: #2d3137 !important;
      visibility: visible;
    }
  }
`

const MenuContainer: React.FC<BoxProps> = ({ children, zIndex, ...props }) => {
  const { isMenuOpen } = useMenuContainer()

  return (
    <StyledContainer id="menu-container" zIndex={zIndex}>
      <StyledInnerContainer $Open={isMenuOpen} {...props} zIndex={zIndex}>
        {children}
      </StyledInnerContainer>
    </StyledContainer>
  )
}

const Menu: React.FC<{ layoutConfig: UIBreakPointConfig<UIComponentConfig> }> = ({ layoutConfig }) => {
  const { isMenuOpen, toggleMenu } = useMenuContainer()
  const { isMd, isSm, isXs, isMobile, isTablet, isDesktop } = useMatchBreakpoints()
  const isMenuCanHide = isMd || isSm || isXs
  const cleanupRef = useRef<() => void>(null)

  useEffect(() => {
    if (isMenuOpen && isMobile) {
      toggleHtmlScrollable(false)
      return () => {
        toggleHtmlScrollable(true)
      }
    }
  }, [isMenuOpen, isMobile])

  useEffect(() => {
    if (isMenuOpen && typeof window !== 'undefined' && window.innerWidth < overlayBreakpointMap.menuOverlay) {
      cleanupRef.current = handleClickElement({
        elementId: 'app-content',
        callback: toggleMenu,
      })
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [isMenuOpen])

  const isHide =
    (isMobile && layoutConfig?.mobile?.hidden) ||
    (isTablet && layoutConfig?.tablet?.hidden) ||
    (isDesktop && layoutConfig?.desktop?.hidden)

  return (
    !isHide && (
      <MenuContainer
        id="menu-navigation"
        minHeight={[
          `calc(100% - var(--notification-bar-height) - ${theme.bottombarHeight})`,
          '',
          'calc(100% - var(--notification-bar-height))',
        ]}
        maxHeight={[
          `calc(100% - var(--notification-bar-height) - ${theme.bottombarHeight})`,
          '',
          'calc(100% - var(--notification-bar-height))',
        ]}
        height={[
          `calc(100% - var(--notification-bar-height) - ${theme.bottombarHeight})`,
          '',
          'calc(100% - var(--notification-bar-height))',
        ]}
      >
        <StyledMenuWrapper flexDirection="column">
          {/* HEADER */}
          <TopbarContainer position="relative">
            <TopbarContainer position="relative" overflow="hidden">
              {isMobile ? (
                <Flex pl="12px" mt="12px">
                  <CasinoSportSelector />
                </Flex>
              ) : (
                <Box
                  as={NextLinkFromReactRouter}
                  to={RouteConfig.Home}
                  overflow="hidden"
                  height="100%"
                  position="relative"
                >
                  {isMenuOpen ? (
                    <StyledTopbarContainer id="hunny-logo-full" $Open={isMenuOpen}>
                      <Image src="/logo-text.png" height={40} width={134.7} alt="logo" />
                    </StyledTopbarContainer>
                  ) : (
                    <RowCenter id="hunny-logo-small" height="100%">
                      <Image src="/logo-hunny-200.png" height={34} width={34} alt="logo" />
                    </RowCenter>
                  )}
                </Box>
              )}
            </TopbarContainer>

            <StyledMenuIconContainer
              id={isMenuOpen ? 'close' : 'open'}
              m="0 2px"
              onClick={toggleMenu}
              $Open={isMenuOpen}
            >
              <RowCenter p="4px">
                <Icons.NavigationLeftFoldIcon width="20px" height="20px" />
              </RowCenter>
            </StyledMenuIconContainer>

            <StyledMenuIconMobileContainer id="close-menu" onClick={toggleMenu} $Open={isMenuOpen}>
              <Icons.ClearIcon width="28px" height="28px" />
            </StyledMenuIconMobileContainer>
          </TopbarContainer>

          <StyledMenuContent overflowX="hidden">
            {isMenuCanHide || isMenuOpen ? <MenuContent small={!isMenuOpen && !isMenuCanHide} /> : <MenuSmallContent />}
            {!isMobile && <BonusHoverTooltip />}
          </StyledMenuContent>
        </StyledMenuWrapper>
      </MenuContainer>
    )
  )
}

export default Menu
